import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { 
  Briefcase, 
  Building2, 
  MapPin, 
  Target, 
  ListChecks, 
  GraduationCap, 
  Building,
  Plus,
  CheckCircle2,
  Edit,
  Trash2,
  Save,
  X
} from 'lucide-react';

const sections = [
  { key: 'profession', label: 'Professions', icon: Briefcase, color: 'from-blue-500 to-blue-600' },
  { key: 'organization', label: 'Organizations', icon: Building2, color: 'from-purple-500 to-purple-600' },
  { key: 'cell', label: 'Cells', icon: MapPin, color: 'from-green-500 to-green-600' },
  { key: 'task', label: 'Tasks', icon: Target, color: 'from-orange-500 to-orange-600' },
  { key: 'subtask', label: 'Subtasks', icon: ListChecks, color: 'from-pink-500 to-pink-600' },
  { key: 'training', label: 'Trainings', icon: GraduationCap, color: 'from-indigo-500 to-indigo-600' },
  { key: 'buildingProject', label: 'Building Projects', icon: Building, color: 'from-teal-500 to-teal-600' },
];

const MasterData = () => {
  const { user, refreshUser } = useAuth();
  const [active, setActive] = useState('profession');
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // { type: 'profession', id: 1, data: {...} }
  const [dd, setDd] = useState({
    professions: [],
    organizations: [],
    cells: [],
    tasks: [],
    subtasks: [],
    trainingModes: [],
    trainings: [],
    states: [],
    districts: [],
    buildingProjects: [],
  });

  const reloadDropdowns = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/masters/dropdowns');
      setDd(data);
    } catch (e) {
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadDropdowns();
  }, []);

  // Handler functions for CSP compliance
  const handleProfessionSubmit = async (e) => {
          e.preventDefault();
          const form = e.currentTarget;
          const formData = new FormData(form);
          const profession = String(formData.get('profession') || '').trim();
          if (!profession) return;
          try {
            await axios.post('/api/masters/professions', { profession });
            toast.success('Profession added');
            form.reset();
            reloadDropdowns();
          } catch (err) {
            toast.error('Failed to add');
          }
  };

  const handleProfessionEditChange = (editingItem, e) => {
    setEditingItem({ ...editingItem, data: { ...editingItem.data, profession: e.target.value } });
  };

  const handleProfessionSave = async (p, editingItem) => {
    const newValue = editingItem.data.profession?.trim();
    if (newValue && newValue !== p.profession) {
      try {
        await axios.put(`/api/masters/professions/${p.profession_id}`, { profession: newValue });
        toast.success('Profession updated');
        reloadDropdowns();
        setEditingItem(null);
      } catch (err) {
        toast.error('Failed to update');
      }
    } else {
      setEditingItem(null);
    }
  };

  const handleProfessionCancel = () => {
    setEditingItem(null);
  };

  const handleProfessionEdit = (p) => {
    setEditingItem({ type: 'profession', id: p.profession_id, data: { profession: p.profession } });
  };

  const handleProfessionDelete = async (p) => {
    if (!window.confirm(`Delete "${p.profession}"?`)) return;
    try {
      await axios.delete(`/api/masters/professions/${p.profession_id}`);
      toast.success('Profession deleted');
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Organization handlers
  const handleOrganizationSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const organizationName = String(formData.get('organizationName') || '').trim();
    if (!organizationName) return;
    try {
      await axios.post('/api/masters/organizations', { organizationName });
      toast.success('Organization added');
      form.reset();
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to add');
    }
  };

  const handleOrganizationEditChange = (editingItem, e) => {
    setEditingItem({ ...editingItem, data: { ...editingItem.data, organization_name: e.target.value } });
  };

  const handleOrganizationSave = async (o, editingItem) => {
    const newValue = editingItem.data.organization_name?.trim();
    if (newValue && newValue !== o.organization_name) {
      try {
        await axios.put(`/api/masters/organizations/${o.organization_id}`, { organizationName: newValue });
        toast.success('Organization updated');
        reloadDropdowns();
        setEditingItem(null);
      } catch (err) {
        toast.error('Failed to update');
      }
    } else {
      setEditingItem(null);
    }
  };

  const handleOrganizationCancel = () => {
    setEditingItem(null);
  };

  const handleOrganizationEdit = (o) => {
    setEditingItem({ type: 'organization', id: o.organization_id, data: { organization_name: o.organization_name } });
  };

  const handleOrganizationDelete = async (o) => {
    if (!window.confirm(`Delete "${o.organization_name}"?`)) return;
    try {
      await axios.delete(`/api/masters/organizations/${o.organization_id}`);
      toast.success('Organization deleted');
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // KeyDown handlers
  const handleEscapeKey = (e) => {
    if (e.key === 'Escape') {
      setEditingItem(null);
    }
  };

  // Generic handlers for all sections
  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  const handleSectionChange = (key) => {
    setActive(key);
    setEditingItem(null);
  };

  // Cell handlers
  const handleCellSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const cellNo = Number(formData.get('cellNo'));
    const stateId = Number(formData.get('stateId'));
    const establishmentDate = String(formData.get('establishmentDate') || '') || null;
    if (!cellNo || !stateId) return;
    try {
      await axios.post('/api/masters/cells', { cellNo, stateId, establishmentDate });
      toast.success('Cell added');
      form.reset();
      reloadDropdowns();
      if (user?.cellId && Number(cellNo) === user.cellNo) {
        await refreshUser();
        toast.success('User data refreshed');
      }
    } catch (err) {
      toast.error('Failed to add');
    }
  };

  const handleCellEdit = async (c) => {
    const newDate = prompt('Enter establishment date (YYYY-MM-DD) or leave empty to remove:', 
      c.cell_establishment_date ? new Date(c.cell_establishment_date).toISOString().split('T')[0] : '');
    if (newDate !== null) {
      try {
        await axios.put(`/api/masters/cells/${c.cell_id}`, { 
          establishmentDate: newDate || null 
        });
        toast.success('Cell updated');
        await reloadDropdowns();
        if (user?.cellId === c.cell_id) {
          await refreshUser();
          toast.success('Your session refreshed');
        }
      } catch (err) {
        toast.error('Failed to update cell');
      }
    }
  };

  const handleCellDelete = async (c) => {
    if (!window.confirm(`Are you sure you want to delete "Cell ${c.cell_no} (${c.state_name})"?`)) return;
    try {
      await axios.delete(`/api/masters/cells/${c.cell_id}`);
      toast.success('Cell deleted');
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Task handlers
  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const taskNo = Number(formData.get('taskNo'));
    const taskDescription = String(formData.get('taskDescription') || '').trim();
    if (!taskNo || !taskDescription) return;
    try {
      await axios.post('/api/masters/tasks', { taskNo, taskDescription });
      toast.success('Task added');
      form.reset();
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to add');
    }
  };

  const handleTaskEditKeyDown = async (e, t) => {
    if (e.key === 'Enter') {
      const taskNo = Number(e.target.parentElement.querySelector('input[type="number"]').value);
      const taskDesc = e.target.value.trim();
      if (taskNo && taskDesc) {
        try {
          await axios.put(`/api/masters/tasks/${t.task_id}`, { taskNo, taskDescription: taskDesc });
          toast.success('Task updated');
          reloadDropdowns();
          setEditingItem(null);
        } catch (err) {
          toast.error('Failed to update');
        }
      }
    } else if (e.key === 'Escape') {
      setEditingItem(null);
    }
  };

  const handleTaskEdit = (t) => {
    setEditingItem({ type: 'task', id: t.task_id, data: t });
  };

  const handleTaskDelete = async (t) => {
    if (!window.confirm(`Are you sure you want to delete "Task ${t.task_no}: ${t.task_description}"?`)) return;
    try {
      await axios.delete(`/api/masters/tasks/${t.task_id}`);
      toast.success('Task deleted');
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Subtask handlers
  const handleSubtaskSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const taskId = Number(formData.get('taskId'));
    const subtaskNo = Number(formData.get('subtaskNo'));
    const subtaskDescription = String(formData.get('subtaskDescription') || '').trim();
    if (!taskId || !subtaskNo || !subtaskDescription) return;
    try {
      await axios.post('/api/masters/subtasks', { taskId, subtaskNo, subtaskDescription });
      toast.success('Subtask added');
      form.reset();
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to add');
    }
  };

  const handleSubtaskEditChange = (editingItem, field, value) => {
    setEditingItem({ ...editingItem, data: { ...editingItem.data, [field]: value } });
  };

  const handleSubtaskSave = async (s, editingItem) => {
    const taskId = editingItem.data.task_id;
    const subtaskNo = editingItem.data.subtask_no;
    const subtaskDesc = editingItem.data.subtask_description?.trim();
    if (taskId && subtaskNo && subtaskDesc) {
      try {
        await axios.put(`/api/masters/subtasks/${s.subtask_id}`, { taskId, subtaskNo, subtaskDescription: subtaskDesc });
        toast.success('Subtask updated');
        reloadDropdowns();
        setEditingItem(null);
      } catch (err) {
        toast.error('Failed to update');
      }
    } else {
      toast.error('Please fill all fields');
    }
  };

  const handleSubtaskEdit = (s) => {
    setEditingItem({ type: 'subtask', id: s.subtask_id, data: { task_id: s.task_id, subtask_no: s.subtask_no, subtask_description: s.subtask_description } });
  };

  const handleSubtaskDelete = async (s) => {
    if (!window.confirm(`Are you sure you want to delete this subtask?`)) return;
    try {
      await axios.delete(`/api/masters/subtasks/${s.subtask_id}`);
      toast.success('Subtask deleted');
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Training handlers
  const handleTrainingSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const trainingType = String(fd.get('trainingType') || '').trim();
    const trainingModeId = Number(fd.get('trainingModeId'));
    const numberOfTrainings = Number(fd.get('numberOfTrainings'));
    if (!trainingType || !trainingModeId || !numberOfTrainings) return;
    try {
      await axios.post('/api/masters/trainings', { trainingType, trainingModeId, numberOfTrainings });
      toast.success('Training added');
      form.reset();
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to add');
    }
  };

  const handleTrainingEditChange = (editingItem, field, value) => {
    setEditingItem({ ...editingItem, data: { ...editingItem.data, [field]: value } });
  };

  const handleTrainingSave = async (t, editingItem) => {
    const trainingType = editingItem.data.training_type?.trim();
    const trainingModeId = editingItem.data.training_mode_id;
    const numberOfTrainings = editingItem.data.number_of_trainings;
    if (trainingType && trainingModeId && numberOfTrainings) {
      try {
        await axios.put(`/api/masters/trainings/${t.training_id}`, { trainingType, trainingModeId, numberOfTrainings });
        toast.success('Training updated');
        reloadDropdowns();
        setEditingItem(null);
      } catch (err) {
        toast.error('Failed to update');
      }
    } else {
      toast.error('Please fill all fields');
    }
  };

  const handleTrainingEdit = (t) => {
    setEditingItem({ type: 'training', id: t.training_id, data: { training_type: t.training_type, training_mode_id: t.training_mode_id, number_of_trainings: t.number_of_trainings } });
  };

  const handleTrainingDelete = async (t) => {
    if (!window.confirm(`Are you sure you want to delete "${t.training_type}"?`)) return;
    try {
      await axios.delete(`/api/masters/trainings/${t.training_id}`);
      toast.success('Training deleted');
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Building Project handlers
  const handleBuildingProjectSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const buildingProjectType = String(fd.get('buildingProjectType') || '').trim();
    const numberOfProjects = Number(fd.get('numberOfProjects'));
    const isLabelling = String(fd.get('isLabelling')) === 'true';
    if (!buildingProjectType || !numberOfProjects) return;
    try {
      await axios.post('/api/masters/building-projects', { buildingProjectType, numberOfProjects, isLabelling });
      toast.success('Building Project added');
      form.reset();
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to add');
    }
  };

  const handleBuildingProjectEditChange = (editingItem, field, value) => {
    setEditingItem({ ...editingItem, data: { ...editingItem.data, [field]: value } });
  };

  const handleBuildingProjectSave = async (b, editingItem) => {
    const buildingProjectType = editingItem.data.building_project_type?.trim();
    const numberOfProjects = editingItem.data.number_of_projects;
    const isLabelling = editingItem.data.islabelling;
    if (buildingProjectType && numberOfProjects) {
      try {
        await axios.put(`/api/masters/building-projects/${b.building_project_id}`, { buildingProjectType, numberOfProjects, isLabelling });
        toast.success('Building project updated');
        reloadDropdowns();
        setEditingItem(null);
      } catch (err) {
        toast.error('Failed to update');
      }
    } else {
      toast.error('Please fill all fields');
    }
  };

  const handleBuildingProjectEdit = (b) => {
    setEditingItem({ type: 'buildingProject', id: b.building_project_id, data: { building_project_type: b.building_project_type, number_of_projects: b.number_of_projects, islabelling: b.islabelling } });
  };

  const handleBuildingProjectDelete = async (b) => {
    if (!window.confirm(`Are you sure you want to delete "${b.building_project_type}"?`)) return;
    try {
      await axios.delete(`/api/masters/building-projects/${b.building_project_id}`);
      toast.success('Building project deleted');
      reloadDropdowns();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const professionUI = (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <Briefcase className="h-6 w-6 mr-2" />
          Add Profession
        </h3>
      </div>
      <div className="p-6">
      <form
        onSubmit={handleProfessionSubmit}
          className="space-y-4"
      >
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Profession</label>
            <input 
              name="profession" 
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
              placeholder="e.g. Architect" 
            />
        </div>
          <button 
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-2.5 px-6 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2" 
            type="submit"
          >
            <Plus className="h-5 w-5" />
            Add Profession
          </button>
      </form>
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
            Existing Professions ({dd.professions.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {dd.professions.map((p) => (
              <div key={p.profession_id} className="px-4 py-3 bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-lg hover:shadow-md transition-all">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    {editingItem?.type === 'profession' && editingItem?.id === p.profession_id ? (
                      <>
                        <input
                          type="text"
                          defaultValue={p.profession}
                          ref={(input) => input && input.focus()}
                          className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onChange={(e) => handleProfessionEditChange(editingItem, e)}
                          onKeyDown={handleEscapeKey}
                        />
                        <button
                          onClick={() => handleProfessionSave(p, editingItem)}
                          className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                          title="Save"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingItem(null)}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <span className="text-sm font-medium text-gray-800">{p.profession}</span>
                    )}
                  </div>
                  {editingItem?.type !== 'profession' || editingItem?.id !== p.profession_id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleProfessionEdit(p)}
                        className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    <button
                      onClick={() => handleProfessionDelete(p)}
                      className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const organizationUI = (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <Building2 className="h-6 w-6 mr-2" />
          Add Organization
        </h3>
      </div>
      <div className="p-6">
      <form
        onSubmit={handleOrganizationSubmit}
          className="space-y-4"
      >
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Organization Name</label>
            <input 
              name="organizationName" 
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all" 
              placeholder="Organization Name" 
            />
        </div>
          <button 
            className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold py-2.5 px-6 rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2" 
            type="submit"
          >
            <Plus className="h-5 w-5" />
            Add Organization
          </button>
      </form>
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
            Existing Organizations ({dd.organizations.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {dd.organizations.map((o) => (
              <div key={o.organization_id} className="px-4 py-3 bg-gradient-to-br from-purple-50 to-white border border-purple-100 rounded-lg hover:shadow-md transition-all">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    {editingItem?.type === 'organization' && editingItem?.id === o.organization_id ? (
                      <>
                        <input
                          type="text"
                          defaultValue={o.organization_name}
                          ref={(input) => input && input.focus()}
                          className="flex-1 px-2 py-1 text-sm border border-purple-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          onChange={(e) => handleOrganizationEditChange(editingItem, e)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setEditingItem(null);
                          }}
                        />
                        <button
                          onClick={() => handleOrganizationSave(o, editingItem)}
                          className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                          title="Save"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingItem(null)}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <span className="text-sm font-medium text-gray-800">{o.organization_name}</span>
                    )}
                  </div>
                  {editingItem?.type !== 'organization' || editingItem?.id !== o.organization_id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOrganizationEdit(o)}
                        className="p-1.5 text-purple-600 hover:bg-purple-100 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleOrganizationDelete(o)}
                        className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const cellUI = (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <MapPin className="h-6 w-6 mr-2" />
          Add Cell
        </h3>
      </div>
      <div className="p-6">
      <form
        onSubmit={handleCellSubmit}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Cell No</label>
          <input name="cellNo" type="number" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
          <select name="stateId" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all">
            <option value="">Select State</option>
            {dd.states.map((s) => (
              <option key={s.state_id} value={s.state_id}>{s.state_ut}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Establishment Date</label>
          <input name="establishmentDate" type="date" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all" />
        </div>
        <div className="md:col-span-3">
          <button 
            className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-2.5 px-6 rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2" 
            type="submit"
          >
            <Plus className="h-5 w-5" />
            Add Cell
          </button>
        </div>
      </form>
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
          Existing Cells ({dd.cells.length})
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dd.cells.map((c) => (
            <div key={c.cell_id} className="px-5 py-4 bg-gradient-to-br from-green-50 to-white border border-green-100 rounded-lg hover:shadow-lg transition-all">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-bold text-gray-900 text-lg mb-1">Cell {c.cell_no}</div>
                  <div className="text-sm text-gray-600 mb-2">{c.state_name}</div>
                  {c.cell_establishment_date ? (
                    <div className="text-xs text-green-600 font-medium flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Established: {new Date(c.cell_establishment_date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-orange-600 font-medium">⚠ No establishment date</div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleCellEdit(c)}
                    className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleCellDelete(c)}
                    className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );

  const taskUI = (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <Target className="h-6 w-6 mr-2" />
          Add Task
        </h3>
      </div>
      <div className="p-6">
      <form
        onSubmit={handleTaskSubmit}
          className="space-y-4"
      >
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Task No</label>
            <input name="taskNo" type="number" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all" />
        </div>
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Task Description</label>
            <input name="taskDescription" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all" />
        </div>
          <button 
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold py-2.5 px-6 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2" 
            type="submit"
          >
            <Plus className="h-5 w-5" />
            Add Task
          </button>
      </form>
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
            Existing Tasks ({dd.tasks.length})
          </h4>
          <div className="space-y-2">
          {dd.tasks.map((t) => (
              <div key={t.task_id} className="px-4 py-3 bg-gradient-to-br from-orange-50 to-white border border-orange-100 rounded-lg hover:shadow-md transition-all">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1">
                    {editingItem?.type === 'task' && editingItem?.id === t.task_id ? (
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          defaultValue={t.task_no}
                          placeholder="Task No"
                          className="px-2 py-1 text-sm border border-orange-300 rounded focus:ring-2 focus:ring-orange-500"
                        />
                        <input
                          type="text"
                          defaultValue={t.task_description}
                          placeholder="Description"
                          className="px-2 py-1 text-sm border border-orange-300 rounded focus:ring-2 focus:ring-orange-500"
                          onKeyDown={(e) => handleTaskEditKeyDown(e, t)}
                        />
                      </div>
                    ) : (
                      <>
                        <span className="font-bold text-orange-600">Task {t.task_no}:</span>
                        <span className="text-sm text-gray-800">{t.task_description}</span>
                      </>
                    )}
                  </div>
                  {editingItem?.type !== 'task' || editingItem?.id !== t.task_id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleTaskEdit(t)}
                        className="p-1.5 text-orange-600 hover:bg-orange-100 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleTaskDelete(t)}
                        className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const subtaskUI = (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-pink-500 to-pink-600 px-6 py-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <ListChecks className="h-6 w-6 mr-2" />
          Add Subtask
        </h3>
      </div>
      <div className="p-6">
      <form
        onSubmit={handleSubtaskSubmit}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Task</label>
            <select name="taskId" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all">
            <option value="">Select Task</option>
            {dd.tasks.map((t) => (
              <option key={t.task_id} value={t.task_id}>Task {t.task_no}: {t.task_description}</option>
            ))}
          </select>
        </div>
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Subtask No</label>
            <input name="subtaskNo" type="number" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all" />
        </div>
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Subtask Description</label>
            <input name="subtaskDescription" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all" />
        </div>
        <div className="md:col-span-3">
            <button 
              className="w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white font-semibold py-2.5 px-6 rounded-lg hover:from-pink-600 hover:to-pink-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2" 
              type="submit"
            >
              <Plus className="h-5 w-5" />
              Add Subtask
            </button>
        </div>
      </form>
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
            Existing Subtasks ({dd.subtasks.length})
          </h4>
          <div className="space-y-2 max-h-96 overflow-y-auto">
          {dd.subtasks.slice(0, 20).map((s) => (
              <div key={s.subtask_id} className="px-4 py-3 bg-gradient-to-br from-pink-50 to-white border border-pink-100 rounded-lg hover:shadow-md transition-all">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1">
                    {editingItem?.type === 'subtask' && editingItem?.id === s.subtask_id ? (
                      <>
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <select
                            defaultValue={s.task_id}
                            className="px-2 py-1 text-sm border border-pink-300 rounded focus:ring-2 focus:ring-pink-500"
                            onChange={(e) => handleSubtaskEditChange(editingItem, 'task_id', Number(e.target.value))}
                          >
                            {dd.tasks.map((task) => (
                              <option key={task.task_id} value={task.task_id}>Task {task.task_no}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            defaultValue={s.subtask_no}
                            placeholder="Subtask No"
                            ref={(input) => input && input.focus()}
                            className="px-2 py-1 text-sm border border-pink-300 rounded focus:ring-2 focus:ring-pink-500"
                            onChange={(e) => handleSubtaskEditChange(editingItem, 'subtask_no', Number(e.target.value))}
                          />
                          <input
                            type="text"
                            defaultValue={s.subtask_description}
                            placeholder="Description"
                            className="px-2 py-1 text-sm border border-pink-300 rounded focus:ring-2 focus:ring-pink-500"
                            onChange={(e) => handleSubtaskEditChange(editingItem, 'subtask_description', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setEditingItem(null);
                              }
                            }}
                          />
                        </div>
                        <button
                          onClick={() => handleSubtaskSave(s, editingItem)}
                          className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                          title="Save"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingItem(null)}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="font-bold text-pink-600">Task {s.task_id} - Subtask {s.subtask_no}:</span>
                        <span className="text-sm text-gray-800">{s.subtask_description}</span>
                      </>
                    )}
                  </div>
                  {editingItem?.type !== 'subtask' || editingItem?.id !== s.subtask_id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleSubtaskEdit(s)}
                        className="p-1.5 text-pink-600 hover:bg-pink-100 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleSubtaskDelete(s)}
                        className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const trainingUI = (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <GraduationCap className="h-6 w-6 mr-2" />
          Add Training
        </h3>
      </div>
      <div className="p-6">
      <form
        onSubmit={handleTrainingSubmit}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Training Type</label>
            <input name="trainingType" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all" />
        </div>
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Mode</label>
            <select name="trainingModeId" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all">
            <option value="">Select Mode</option>
            {dd.trainingModes.map((m) => (
              <option key={m.training_mode_id} value={m.training_mode_id}>{m.training_mode}</option>
            ))}
          </select>
        </div>
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Trainings</label>
            <input name="numberOfTrainings" type="number" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all" />
        </div>
        <div className="md:col-span-3">
            <button 
              className="w-full bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-semibold py-2.5 px-6 rounded-lg hover:from-indigo-600 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2" 
              type="submit"
            >
              <Plus className="h-5 w-5" />
              Add Training
            </button>
        </div>
      </form>
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
            Existing Trainings ({dd.trainings.length})
          </h4>
          <div className="space-y-2 max-h-96 overflow-y-auto">
          {dd.trainings.slice(0, 20).map((t) => (
              <div key={t.training_id} className="px-4 py-3 bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 rounded-lg hover:shadow-md transition-all">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1">
                    {editingItem?.type === 'training' && editingItem?.id === t.training_id ? (
                      <>
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            defaultValue={t.training_type}
                            placeholder="Training Type"
                            ref={(input) => input && input.focus()}
                            className="px-2 py-1 text-sm border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500"
                            onChange={(e) => handleTrainingEditChange(editingItem, 'training_type', e.target.value)}
                          />
                          <select
                            defaultValue={t.training_mode_id}
                            className="px-2 py-1 text-sm border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500"
                            onChange={(e) => handleTrainingEditChange(editingItem, 'training_mode_id', Number(e.target.value))}
                          >
                            {dd.trainingModes.map((m) => (
                              <option key={m.training_mode_id} value={m.training_mode_id}>{m.training_mode}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            defaultValue={t.number_of_trainings}
                            placeholder="Number"
                            className="px-2 py-1 text-sm border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500"
                            onChange={(e) => handleTrainingEditChange(editingItem, 'number_of_trainings', Number(e.target.value))}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setEditingItem(null);
                              }
                            }}
                          />
                        </div>
                        <button
                          onClick={() => handleTrainingSave(t, editingItem)}
                          className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                          title="Save"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingItem(null)}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <div className="flex items-center justify-between flex-1">
                        <div>
                          <span className="font-semibold text-indigo-700">{t.training_type}</span>
                          <span className="text-sm text-gray-600 ml-2">(Mode: {t.training_mode})</span>
                        </div>
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">Limit: {t.number_of_trainings}</span>
                      </div>
                    )}
                  </div>
                  {editingItem?.type !== 'training' || editingItem?.id !== t.training_id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleTrainingEdit(t)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-100 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleTrainingDelete(t)}
                        className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const buildingProjectUI = (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <Building className="h-6 w-6 mr-2" />
          Add Building Project
        </h3>
      </div>
      <div className="p-6">
      <form
        onSubmit={handleBuildingProjectSubmit}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
            <input name="buildingProjectType" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" />
        </div>
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Projects</label>
            <input name="numberOfProjects" type="number" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" />
        </div>
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Is Labelling</label>
            <select name="isLabelling" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all">
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
        <div className="md:col-span-3">
            <button 
              className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white font-semibold py-2.5 px-6 rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2" 
              type="submit"
            >
              <Plus className="h-5 w-5" />
              Add Building Project
            </button>
        </div>
      </form>
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
            Existing Building Projects ({dd.buildingProjects.length})
          </h4>
          <div className="space-y-2 max-h-96 overflow-y-auto">
          {dd.buildingProjects.slice(0, 20).map((b) => (
              <div key={b.building_project_id} className="px-4 py-3 bg-gradient-to-br from-teal-50 to-white border border-teal-100 rounded-lg hover:shadow-md transition-all">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1">
                    {editingItem?.type === 'buildingProject' && editingItem?.id === b.building_project_id ? (
                      <>
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            defaultValue={b.building_project_type}
                            placeholder="Type"
                            ref={(input) => input && input.focus()}
                            className="px-2 py-1 text-sm border border-teal-300 rounded focus:ring-2 focus:ring-teal-500"
                            onChange={(e) => handleBuildingProjectEditChange(editingItem, 'building_project_type', e.target.value)}
                          />
                          <input
                            type="number"
                            defaultValue={b.number_of_projects}
                            placeholder="Number"
                            className="px-2 py-1 text-sm border border-teal-300 rounded focus:ring-2 focus:ring-teal-500"
                            onChange={(e) => handleBuildingProjectEditChange(editingItem, 'number_of_projects', Number(e.target.value))}
                          />
                          <select
                            defaultValue={b.islabelling ? 'true' : 'false'}
                            className="px-2 py-1 text-sm border border-teal-300 rounded focus:ring-2 focus:ring-teal-500"
                            onChange={(e) => {
                              setEditingItem({ ...editingItem, data: { ...editingItem.data, islabelling: e.target.value === 'true' } });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setEditingItem(null);
                              }
                            }}
                          >
                            <option value="false">No</option>
                            <option value="true">Yes</option>
                          </select>
                        </div>
                        <button
                          onClick={async () => {
                            const buildingProjectType = editingItem.data.building_project_type?.trim();
                            const numberOfProjects = editingItem.data.number_of_projects;
                            const isLabelling = editingItem.data.islabelling;
                            if (buildingProjectType && numberOfProjects) {
                              try {
                                await axios.put(`/api/masters/building-projects/${b.building_project_id}`, { buildingProjectType, numberOfProjects, isLabelling });
                                toast.success('Building project updated');
                                reloadDropdowns();
                                setEditingItem(null);
                              } catch (err) {
                                toast.error('Failed to update');
                              }
                            } else {
                              toast.error('Please fill all fields');
                            }
                          }}
                          className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                          title="Save"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingItem(null)}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <div className="flex items-center justify-between flex-1">
                        <div>
                          <span className="font-semibold text-teal-700">{b.building_project_type}</span>
                          {b.islabelling && (
                            <span className="ml-2 px-2 py-0.5 bg-teal-200 text-teal-800 rounded text-xs font-medium">Labelling</span>
                          )}
                        </div>
                        <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">Limit: {b.number_of_projects}</span>
                      </div>
                    )}
                  </div>
                  {editingItem?.type !== 'buildingProject' || editingItem?.id !== b.building_project_id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingItem({ type: 'buildingProject', id: b.building_project_id, data: { building_project_type: b.building_project_type, number_of_projects: b.number_of_projects, islabelling: b.islabelling } })}
                        className="p-1.5 text-teal-600 hover:bg-teal-100 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={async () => {
                          if (window.confirm(`Are you sure you want to delete "${b.building_project_type}"?`)) {
                            try {
                              await axios.delete(`/api/masters/building-projects/${b.building_project_id}`);
                              toast.success('Building project deleted');
                              reloadDropdowns();
                            } catch (err) {
                              toast.error('Failed to delete');
                            }
                          }
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const body = useMemo(() => {
    switch (active) {
      case 'profession': return professionUI;
      case 'organization': return organizationUI;
      case 'cell': return cellUI;
      case 'task': return taskUI;
      case 'subtask': return subtaskUI;
      case 'training': return trainingUI;
      case 'buildingProject': return buildingProjectUI;
      default: return null;
    }
  }, [active, dd]);

  if (user?.role !== 'Super-Admin') {
    return (
      <div className="card">
        <p className="text-gray-700">Only Super-Admin can access Master Data management.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Master Data Management</h1>
        <p className="text-gray-300">Manage and configure system master data</p>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200">
      <div className="flex flex-wrap gap-2">
          {sections.map((s) => {
            const Icon = s.icon;
            const isActive = active === s.key;
            return (
          <button
            key={s.key}
            onClick={() => setActive(s.key)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 transform ${
                  isActive
                    ? `bg-gradient-to-r ${s.color} text-white shadow-lg scale-105`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-102'
                }`}
              >
                <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-600'}`} />
            {s.label}
          </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : body}
    </div>
  );
};

export default MasterData;



